<template>
  <v-dialog
    v-model="dialog"
    :max-width="$vuetify.breakpoint.mobile ? '95vw' : '1000px'"
    :fullscreen="$vuetify.breakpoint.xs"
    persistent
    transition="dialog-bottom-transition"
    class="modern-dialog"
  >
    <v-card
      class="modern-card elevation-24"
      :class="{ 'fullscreen-card': $vuetify.breakpoint.xs }"
    >
      <!-- Glassmorphism Header -->
      <div class="header-container">
        <div class="header-background"></div>
        <div class="header-content">
          <div class="header-left">
            <div class="icon-container">
              <v-icon
                :size="$vuetify.breakpoint.mobile ? 28 : 32"
                color="white"
                class="header-icon"
              >
                {{ isEdit ? "mdi-account-edit" : "mdi-account-plus" }}
              </v-icon>
            </div>
            <div class="header-text">
              <h2 class="header-title">
                {{ isEdit ? "Edit Administrator" : "Create Administrator" }}
              </h2>
              <p class="header-subtitle">
                {{
                  isEdit
                    ? "Update administrator details"
                    : "Add a new team member"
                }}
              </p>
            </div>
          </div>
          <v-btn
            icon
            large
            @click="closeDialog()"
            class="close-btn"
            color="white"
          >
            <v-icon size="24">mdi-close</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Scrollable Content -->
      <v-card-text class="content-container">
        <v-form ref="form" v-model="valid" lazy-validation>
          <div class="form-sections">
            <!-- Enhanced Avatar Section -->
            <div class="avatar-section-container">
              <div class="avatar-wrapper">
                <div class="avatar-container" @click="triggerFileInput">
                  <v-avatar
                    :size="$vuetify.breakpoint.mobile ? 100 : 120"
                    class="avatar-main"
                    :class="{ 'has-image': avatarUrl }"
                  >
                    <v-img
                      v-if="avatarUrl"
                      :src="avatarUrl"
                      cover
                      class="avatar-image"
                    >
                      <template v-slot:placeholder>
                        <div class="avatar-loading">
                          <v-progress-circular
                            indeterminate
                            color="primary"
                            size="40"
                          ></v-progress-circular>
                        </div>
                      </template>
                    </v-img>
                    <div v-else class="avatar-placeholder">
                      <v-icon
                        :size="$vuetify.breakpoint.mobile ? 40 : 50"
                        color="grey lighten-2"
                      >
                        mdi-account-circle
                      </v-icon>
                    </div>
                  </v-avatar>

                  <!-- Upload Overlay -->
                  <div class="upload-overlay">
                    <v-icon color="white" size="24">mdi-camera-plus</v-icon>
                  </div>
                </div>

                <!-- Avatar Actions -->
                <div class="avatar-actions">
                  <v-file-input
                    ref="fileInput"
                    v-model="selectedFile"
                    accept="image/*"
                    style="display: none"
                    @change="handleImageUpload"
                  />
                  <v-chip
                    small
                    outlined
                    color="primary"
                    @click="triggerFileInput"
                    class="action-chip"
                  >
                    <v-icon left small>mdi-camera</v-icon>
                    {{ avatarUrl ? "Change Photo" : "Upload Photo" }}
                  </v-chip>
                  <v-chip
                    v-if="avatarUrl"
                    small
                    outlined
                    color="error"
                    @click="removeAvatar"
                    class="action-chip"
                  >
                    <v-icon left small>mdi-delete-outline</v-icon>
                    Remove
                  </v-chip>
                </div>
              </div>
            </div>

            <!-- Form Sections with Modern Cards -->
            <div class="form-cards">
              <!-- Personal Information -->
              <div class="form-card personal-card">
                <div class="card-header">
                  <div class="card-icon">
                    <v-icon color="primary">mdi-account</v-icon>
                  </div>
                  <div class="card-title">
                    <h3>Personal Information</h3>
                    <p>Basic details and identification</p>
                  </div>
                </div>

                <div class="card-content">
                  <v-row dense>
                    <v-col cols="12" md="6">
                      <div class="input-group">
                        <v-text-field
                          v-model="adminData.first_name"
                          label="First Name"
                          outlined
                          dense
                          required
                          :rules="nameRules"
                          prepend-inner-icon="mdi-account"
                          class="modern-input"
                          hide-details="auto"
                        />
                      </div>
                    </v-col>

                    <v-col cols="12" md="6">
                      <div class="input-group">
                        <v-text-field
                          v-model="adminData.last_name"
                          label="Last Name"
                          outlined
                          dense
                          required
                          :rules="nameRules"
                          prepend-inner-icon="mdi-account"
                          class="modern-input"
                          hide-details="auto"
                        />
                      </div>
                    </v-col>
                  </v-row>
                </div>
              </div>

              <!-- Authentication -->
              <div class="form-card auth-card">
                <div class="card-header">
                  <div class="card-icon">
                    <v-icon color="primary">mdi-shield-account</v-icon>
                  </div>
                  <div class="card-title">
                    <h3>Authentication</h3>
                    <p>Login credentials and permissions</p>
                  </div>
                </div>

                <div class="card-content">
                  <v-row dense>
                    <v-col cols="12" md="6">
                      <div class="input-group">
                        <v-text-field
                          v-model="adminData.email"
                          label="Email Address"
                          type="email"
                          outlined
                          dense
                          required
                          :rules="emailRules"
                          prepend-inner-icon="mdi-email"
                          class="modern-input"
                          hide-details="auto"
                        />
                      </div>
                    </v-col>

                    <v-col cols="12" md="6" v-if="!isEdit">
                      <div class="input-group">
                        <v-text-field
                          v-model="adminData.password"
                          label="Password"
                          :type="showPassword ? 'text' : 'password'"
                          outlined
                          dense
                          required
                          :rules="passwordRules"
                          prepend-inner-icon="mdi-lock"
                          :append-icon="
                            showPassword ? 'mdi-eye' : 'mdi-eye-off'
                          "
                          @click:append="showPassword = !showPassword"
                          class="modern-input"
                          hide-details="auto"
                        />
                      </div>
                    </v-col>

                    <v-col :cols="isEdit ? 12 : 12" :md="isEdit ? 6 : 12">
                      <div class="input-group">
                        <v-select
                          v-model="adminData.type"
                          :items="roleOptions"
                          label="Administrator Role"
                          outlined
                          dense
                          required
                          :rules="[(v) => !!v || 'Role is required']"
                          prepend-inner-icon="mdi-shield-crown"
                          class="modern-input"
                          hide-details="auto"
                        />
                      </div>
                    </v-col>
                  </v-row>
                </div>
              </div>

              <!-- Contact Information -->
              <div class="form-card contact-card">
                <div class="card-header">
                  <div class="card-icon">
                    <v-icon color="primary">mdi-phone</v-icon>
                  </div>
                  <div class="card-title">
                    <h3>Contact Details</h3>
                    <p>Phone and communication preferences</p>
                  </div>
                </div>

                <div class="card-content">
                  <v-row dense>
                    <v-col cols="12" md="6">
                      <div class="input-group">
                        <v-text-field
                          v-model="adminData.phone"
                          label="Phone Number"
                          outlined
                          dense
                          prepend-inner-icon="mdi-phone"
                          class="modern-input"
                          hide-details="auto"
                          placeholder="+1 (555) 123-4567"
                        />
                      </div>
                    </v-col>
                  </v-row>
                </div>
              </div>

              <!-- Address Information -->
              <div class="form-card address-card">
                <div class="card-header">
                  <div class="card-icon">
                    <v-icon color="primary">mdi-map-marker</v-icon>
                  </div>
                  <div class="card-title">
                    <h3>Address Information</h3>
                    <p>Location and postal details</p>
                  </div>
                </div>

                <div class="card-content">
                  <v-row dense>
                    <v-col cols="12">
                      <div class="input-group">
                        <v-text-field
                          v-model="adminData.address"
                          label="Street Address"
                          outlined
                          dense
                          prepend-inner-icon="mdi-home"
                          class="modern-input"
                          hide-details="auto"
                        />
                      </div>
                    </v-col>

                    <v-col cols="12">
                      <div class="input-group">
                        <v-text-field
                          v-model="adminData.address2"
                          label="Apartment, suite, etc. (optional)"
                          outlined
                          dense
                          prepend-inner-icon="mdi-home-plus"
                          class="modern-input"
                          hide-details="auto"
                        />
                      </div>
                    </v-col>

                    <v-col cols="12" sm="6" md="4">
                      <div class="input-group">
                        <v-text-field
                          v-model="adminData.city"
                          label="City"
                          outlined
                          dense
                          prepend-inner-icon="mdi-city"
                          class="modern-input"
                          hide-details="auto"
                        />
                      </div>
                    </v-col>

                    <v-col cols="12" sm="6" md="4">
                      <div class="input-group">
                        <v-text-field
                          v-model="adminData.state"
                          label="State/Province"
                          outlined
                          dense
                          prepend-inner-icon="mdi-map"
                          class="modern-input"
                          hide-details="auto"
                        />
                      </div>
                    </v-col>

                    <v-col cols="12" md="4">
                      <div class="input-group">
                        <v-text-field
                          v-model="adminData.zip"
                          label="ZIP/Postal Code"
                          outlined
                          dense
                          prepend-inner-icon="mdi-mailbox"
                          class="modern-input"
                          hide-details="auto"
                        />
                      </div>
                    </v-col>
                  </v-row>
                </div>
              </div>
            </div>
          </div>
        </v-form>
      </v-card-text>

      <!-- Modern Action Bar -->
      <div class="action-bar">
        <div class="action-content">
          <v-btn
            text
            large
            @click="closeDialog"
            class="cancel-btn"
            color="grey darken-1"
          >
            <v-icon left>mdi-cancel</v-icon>
            Cancel
          </v-btn>

          <v-btn
            :color="isEdit ? 'primary' : 'success'"
            large
            depressed
            @click="saveAdmin"
            :loading="saving"
            :disabled="!valid"
            class="save-btn"
            min-width="180"
          >
            <v-icon left>{{ isEdit ? "mdi-content-save" : "mdi-plus" }}</v-icon>
            {{ isEdit ? "Update Administrator" : "Create Administrator" }}
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from "vuex";

export default {
  name: "AdminUserDialog",
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    adminData: {
      type: Object,
      default: () => ({}),
    },
    onClose: Function,
    onSave: Function,
  },
  data() {
    return {
      valid: false,
      saving: false,
      showPassword: false,
      avatarUrl: null,
      selectedFile: null,
      // Remove the formData object and use adminData directly
      roleOptions: [
        { text: "Super Admin", value: "superadmin" },
        { text: "Admin", value: "Admin" },
        { text: "Moderator", value: "moderator" },
      ],
      nameRules: [
        (v) => !!v || "This field is required",
        (v) => (v && v.length >= 2) || "Name must be at least 2 characters",
        (v) => (v && v.length <= 50) || "Name must be less than 50 characters",
        (v) =>
          /^[a-zA-Z\s]*$/.test(v) || "Name can only contain letters and spaces",
      ],
      emailRules: [
        (v) => !!v || "Email is required",
        (v) => /.+@.+\..+/.test(v) || "Email must be valid",
        (v) =>
          (v && v.length <= 100) || "Email must be less than 100 characters",
      ],
      passwordRules: [
        (v) => !!v || "Password is required",
        (v) => (v && v.length >= 8) || "Password must be at least 8 characters",
        (v) =>
          (v && v.length <= 50) || "Password must be less than 50 characters",
        (v) =>
          /(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(v) ||
          "Password must contain uppercase, lowercase, number and special character",
      ],
    };
  },
  watch: {
    dialog(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          if (this.isEdit && this.adminData) {
            this.loadUserData();
          } else {
            this.resetForm();
          }
        });
      }
    },
  },
  methods: {
    ...mapActions("adminDashboard", ["createAdmin", "editAdmin"]),

    loadUserData() {
      // No need to copy to formData, we'll use adminData directly
      if (this.adminData.image && typeof this.adminData.image === "string") {
        this.avatarUrl = this.adminData.image;
      }
    },

    resetForm() {
      // Reset the adminData object to empty values
      Object.keys(this.adminData).forEach((key) => {
        this.$set(this.adminData, key, "");
      });

      // Set default values for type
      this.$set(this.adminData, "type", "Admin");

      this.avatarUrl = null;
      this.selectedFile = null;
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },

    triggerFileInput() {
      if (this.$refs.fileInput && this.$refs.fileInput.$refs.input) {
        this.$refs.fileInput.$refs.input.click();
      }
    },

    handleImageUpload(file) {
      const selectedFile = Array.isArray(file) ? file[0] : file;
      if (!selectedFile) return;

      // Enhanced validation
      const maxSize = 5 * 1024 * 1024; // 5MB
      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/webp",
        "image/gif",
      ];

      if (!allowedTypes.includes(selectedFile.type)) {
        this.$emit("show-snackbar", {
          message: "Please upload a valid image file (JPEG, PNG, WebP, or GIF)",
          color: "error",
        });
        return;
      }

      if (selectedFile.size > maxSize) {
        this.$emit("show-snackbar", {
          message: "Image size must be less than 5MB",
          color: "error",
        });
        return;
      }

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        this.avatarUrl = e.target.result;
      };
      reader.readAsDataURL(selectedFile);

      // Store file for backend - add it to adminData
      this.$set(this.adminData, "image", selectedFile);
    },

    removeAvatar() {
      this.selectedFile = null;
      this.avatarUrl = null;
      this.$set(this.adminData, "image", null);

      // Clear file input
      if (this.$refs.fileInput) {
        this.$refs.fileInput.internalValue = null;
      }
    },

    closeDialog() {
      this.$emit("update:dialog", false);
      if (this.onClose) this.onClose();
    },

    async saveAdmin() {
      if (!this.$refs.form.validate()) {
        this.$emit("show-snackbar", {
          message: "Please fix the errors in the form",
          color: "warning",
        });
        return;
      }

      this.saving = true;
      try {
        let response;

        // Create FormData object
        const formData = new FormData();

        // Append all adminData fields
        for (const key in this.adminData) {
          if (
            this.adminData[key] !== undefined &&
            this.adminData[key] !== null
          ) {
            formData.append(key, this.adminData[key]);
          }
        }

        // Ensure password is present when creating
        if (!this.isEdit && !this.adminData.password) {
          this.$emit("show-snackbar", {
            message: "Password is required for new administrators",
            color: "error",
          });
          this.saving = false;
          return;
        }

        // Call Vuex actions with FormData
        if (this.isEdit) {
          response = await this.editAdmin(formData);
        } else {
          response = await this.createAdmin(formData);
        }

        if (this.onSave) this.onSave(response);

        this.$emit("show-snackbar", {
          message: `Administrator ${
            this.isEdit ? "updated" : "created"
          } successfully!`,
          color: "success",
        });

        this.closeDialog();
      } catch (error) {
        console.error("Save admin error:", error);
        this.$emit("show-snackbar", {
          message: error.message || "An error occurred. Please try again.",
          color: "error",
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
/* Core Dialog Styles */
.modern-dialog {
  z-index: 1000;
}

.modern-card {
  border-radius: 24px !important;
  overflow: hidden;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15),
    0 0 0 1px rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

.fullscreen-card {
  border-radius: 0 !important;
  height: 100vh;
  max-height: 100vh;
}

/* Glassmorphism Header */
.header-container {
  position: relative;
  height: 120px;
  overflow: hidden;
  flex-shrink: 0;
}

.header-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #084468 0%, #1da1f2 50%, #3b7ca1 100%);
  backdrop-filter: blur(20px);
}

.header-background::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><g fill="%23ffffff" fill-opacity="0.1"><circle cx="30" cy="30" r="4"/></g></svg>');
  animation: float 6s ease-in-out infinite;
}

.header-content {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 32px;
  height: 100%;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.icon-container {
  width: 48px;
  height: 48px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.header-icon {
  animation: pulse 2s infinite;
}

.header-text {
  color: white;
}

.header-title {
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  line-height: 1.2;
}

.header-subtitle {
  font-size: 14px;
  opacity: 0.9;
  margin: 4px 0 0 0;
  font-weight: 400;
}

.close-btn {
  background: rgba(255, 255, 255, 0.15) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.25) !important;
  transform: scale(1.05);
}

/* Content Area */
.content-container {
  padding: 32px;
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1 1 auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(99, 102, 241, 0.3) transparent;
}

.content-container::-webkit-scrollbar {
  width: 6px;
}

.content-container::-webkit-scrollbar-track {
  background: transparent;
}

.content-container::-webkit-scrollbar-thumb {
  background: rgba(99, 102, 241, 0.3);
  border-radius: 3px;
}

.content-container::-webkit-scrollbar-thumb:hover {
  background: rgba(99, 102, 241, 0.5);
}

/* Avatar Section */
.avatar-section-container {
  display: flex;
  justify-content: center;
  margin-bottom: 40px;
}

.avatar-wrapper {
  text-align: center;
}

.avatar-container {
  position: relative;
  display: inline-block;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.avatar-container:hover {
  transform: scale(1.05);
}

.avatar-main {
  position: relative;
  border: 4px solid #ffffff;
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.avatar-main.has-image {
  border-color: rgba(99, 102, 241, 0.3);
}

.avatar-image {
  border-radius: 50%;
  transition: all 0.3s ease;
}

.avatar-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.avatar-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.upload-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(99, 102, 241, 0.8);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(4px);
}

.avatar-container:hover .upload-overlay {
  opacity: 1;
}

.avatar-actions {
  margin-top: 16px;
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
}

.action-chip {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
}

.action-chip:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

/* Form Cards */
.form-cards {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.form-card {
  background: linear-gradient(135deg, #ffffff 0%, #fefefe 100%);
  border-radius: 20px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.form-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
  border-color: rgba(99, 102, 241, 0.3);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px 24px 16px 24px;
  border-bottom: 1px solid rgba(226, 232, 240, 0.6);
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.card-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: linear-gradient(
    135deg,
    rgba(99, 102, 241, 0.1) 0%,
    rgba(168, 85, 247, 0.1) 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(99, 102, 241, 0.2);
}

.card-title h3 {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: #1e293b;
  line-height: 1.3;
}

.card-title p {
  font-size: 13px;
  color: #64748b;
  margin: 4px 0 0 0;
  font-weight: 400;
}

.card-content {
  padding: 24px;
}

/* Input Styling */
.input-group {
  margin-bottom: 4px;
}

.modern-input {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modern-input:not(.v-input--is-focused):hover {
  transform: translateY(-1px);
}

.modern-input.v-input--is-focused {
  transform: translateY(-2px);
}

.modern-input .v-input__control .v-input__slot {
  background: linear-gradient(135deg, #ffffff 0%, #fefefe 100%);
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modern-input.v-input--is-focused .v-input__control .v-input__slot {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  background: #ffffff;
}

.modern-input .v-input__control .v-input__slot:hover {
  border-color: #cbd5e1;
}

/* Action Bar */
.action-bar {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-top: 1px solid rgba(226, 232, 240, 0.8);
  padding: 24px 32px 32px 32px;
  backdrop-filter: blur(10px);
  flex-shrink: 0;
}

.action-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.cancel-btn {
  min-width: 120px;
  height: 48px;
  border-radius: 12px;
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.cancel-btn:hover {
  background: rgba(107, 114, 128, 0.1);
  transform: translateY(-1px);
}

.save-btn {
  height: 48px;
  border-radius: 12px;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0.025em;
  box-shadow: 0 4px 20px rgba(99, 102, 241, 0.3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: linear-gradient(135deg, #084468 0%, #1da1f2 50%, #3b7ca1 100%);
}

.save-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(99, 102, 241, 0.4);
}

.save-btn:disabled {
  opacity: 0.6;
  transform: none;
  box-shadow: none;
}

.save-btn.success--text {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.save-btn.success--text:hover {
  box-shadow: 0 8px 30px rgba(16, 185, 129, 0.4);
}

/* Animations */
@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

@keyframes slideInUp {
  from {
    transform: translateY(30px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.form-card {
  animation: slideInUp 0.6s ease-out;
}

.form-card:nth-child(1) {
  animation-delay: 0.1s;
}
.form-card:nth-child(2) {
  animation-delay: 0.2s;
}
.form-card:nth-child(3) {
  animation-delay: 0.3s;
}
.form-card:nth-child(4) {
  animation-delay: 0.4s;
}

/* Responsive Design */
@media (max-width: 960px) {
  .modern-card {
    max-height: 95vh;
  }

  .header-content {
    padding: 20px 24px;
  }

  .header-title {
    font-size: 20px;
  }

  .content-container {
    padding: 24px;
  }

  .action-bar {
    padding: 20px 24px 28px 24px;
  }
}

@media (max-width: 600px) {
  .modern-card {
    max-height: 100vh;
  }

  .header-content {
    padding: 16px 20px;
  }

  .header-left {
    gap: 12px;
  }

  .icon-container {
    width: 40px;
    height: 40px;
    border-radius: 12px;
  }

  .header-title {
    font-size: 18px;
  }

  .header-subtitle {
    font-size: 12px;
  }

  .content-container {
    padding: 20px;
  }

  .form-cards {
    gap: 24px;
  }

  .form-card {
    border-radius: 16px;
  }

  .card-header {
    padding: 20px 20px 12px 20px;
    gap: 12px;
  }

  .card-icon {
    width: 36px;
    height: 36px;
  }

  .card-title h3 {
    font-size: 16px;
  }

  .card-title p {
    font-size: 12px;
  }

  .card-content {
    padding: 20px;
  }

  .action-bar {
    padding: 16px 20px 24px 20px;
  }

  .action-content {
    flex-direction: column;
    gap: 12px;
  }

  .cancel-btn,
  .save-btn {
    width: 100%;
    min-width: auto;
  }

  .avatar-actions {
    gap: 8px;
  }

  .action-chip {
    font-size: 12px;
  }
}

@media (max-width: 400px) {
  .content-container {
    padding: 16px;
  }

  .form-cards {
    gap: 20px;
  }

  .card-header {
    padding: 16px 16px 8px 16px;
    flex-direction: column;
    align-items: flex-start;
    text-align: center;
  }

  .card-content {
    padding: 16px;
  }

  .action-bar {
    padding: 12px 16px 20px 16px;
  }
}

/* Dark Theme Support */
.theme--dark .modern-card {
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  border-color: rgba(148, 163, 184, 0.2);
}

.theme--dark .form-card {
  background: linear-gradient(135deg, #334155 0%, #475569 100%);
  border-color: rgba(148, 163, 184, 0.2);
}

.theme--dark .form-card:hover {
  border-color: rgba(99, 102, 241, 0.4);
}

.theme--dark .card-header {
  background: linear-gradient(135deg, #475569 0%, #64748b 100%);
  border-color: rgba(148, 163, 184, 0.2);
}

.theme--dark .card-title h3 {
  color: #f1f5f9;
}

.theme--dark .card-title p {
  color: #cbd5e1;
}

.theme--dark .card-icon {
  background: linear-gradient(135deg, #084468 0%, #1da1f2 50%, #3b7ca1 100%);
  border-color: rgba(99, 102, 241, 0.3);
}

.theme--dark .action-bar {
  background: linear-gradient(135deg, #374151 0%, #4b5563 100%);
  border-color: rgba(148, 163, 184, 0.2);
}

.theme--dark .modern-input .v-input__control .v-input__slot {
  background: linear-gradient(135deg, #475569 0%, #64748b 100%);
  border-color: rgba(148, 163, 184, 0.3);
}

.theme--dark .modern-input .v-input__control .v-input__slot:hover {
  border-color: rgba(148, 163, 184, 0.5);
}

.theme--dark
  .modern-input.v-input--is-focused
  .v-input__control
  .v-input__slot {
  background: #475569;
  border-color: #6366f1;
}

/* Loading States */
.v-btn--loading {
  pointer-events: none;
  opacity: 0.8;
}

.v-btn--loading::before {
  background: rgba(255, 255, 255, 0.1);
}

/* Focus Management */
.modern-input .v-input__control .v-input__slot:focus-within {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

/* Error States */
.modern-input.error--text .v-input__control .v-input__slot {
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

/* Success States */
.modern-input.success--text .v-input__control .v-input__slot {
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

/* Accessibility Improvements */
.close-btn:focus,
.cancel-btn:focus,
.save-btn:focus,
.action-chip:focus {
  outline: 2px solid #6366f1;
  outline-offset: 2px;
}

/* High Contrast Mode */
@media (prefers-contrast: high) {
  .modern-card {
    border: 2px solid #000;
  }

  .form-card {
    border: 2px solid #666;
  }

  .modern-input .v-input__control .v-input__slot {
    border: 2px solid #333;
  }

  .modern-input.v-input--is-focused .v-input__control .v-input__slot {
    border: 2px solid #0066cc;
  }
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }

  .form-card {
    animation: none;
  }

  .header-background::before {
    animation: none;
  }

  .header-icon {
    animation: none;
  }
}
</style>

